import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useReservationPaymentReconciliationPage } from '../Hooks/useReservationPaymentReconciliationPage';
import type { UnreconciledInvoiceRowDto } from '../Types/reports.types';

const fmt = (amount: number) =>
  amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ReservationPaymentReconciliationPage: React.FC = () => {
  const {
    data,
    isLoading,
    isFetching,
    queryErrorMessage,
    filters,
    selectedIds,
    selectedRows,
    previewRows,
    dialogOpen,
    isReconciling,
    reconcileError,
    openSingleDialog,
    openBatchDialog,
    closeDialog,
    confirmReconcile,
    toggleSelection,
    handleFilterChange,
    handlePageChange,
  } = useReservationPaymentReconciliationPage();

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ReceiptLongIcon color="primary" fontSize="large" />
        <Typography variant="h6">Nierozliczone faktury — wpłaty rezerwacji</Typography>
      </Stack>

      {/* Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          label="Szukaj (Nr rezerwacji lub nabywca)"
          size="small"
          value={filters.search ?? ''}
          onChange={(e) => handleFilterChange({ search: e.target.value || undefined })}
          sx={{ minWidth: 280 }}
          data-testid="search-input"
        />
        <TextField
          label="Data od"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filters.issueDateFrom ?? ''}
          onChange={(e) => handleFilterChange({ issueDateFrom: e.target.value || undefined })}
        />
        <TextField
          label="Data do"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filters.issueDateTo ?? ''}
          onChange={(e) => handleFilterChange({ issueDateTo: e.target.value || undefined })}
        />
      </Stack>

      {selectedIds.length > 0 && (
        <Box>
          <Button
            variant="contained"
            onClick={() => openBatchDialog(selectedRows)}
            data-testid="reconcile-batch-btn"
          >
            Rozlicz zaznaczone ({selectedIds.length})
          </Button>
        </Box>
      )}

      {queryErrorMessage && (
        <Alert severity="error" data-testid="error-state">
          {queryErrorMessage}
        </Alert>
      )}

      {(isLoading || isFetching) && !queryErrorMessage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }} data-testid="loading-state">
          <CircularProgress />
        </Box>
      )}

      {!isLoading && !queryErrorMessage && data?.items.length === 0 && (
        <Typography color="text.secondary" data-testid="empty-state">
          Brak nierozliczonych faktur z dostępnymi wpłatami rezerwacji.
        </Typography>
      )}

      {!isLoading && !queryErrorMessage && data && data.items.length > 0 && (
        <Box data-testid="results-state">
          {data.isTruncated && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Wyniki ograniczone do 500 kandydatów. Użyj filtrów, aby zawęzić zakres.
            </Alert>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Nr FV</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Rezerwacja</TableCell>
                <TableCell>Nabywca</TableCell>
                <TableCell align="right">Brutto</TableCell>
                <TableCell align="right">Zapłacono</TableCell>
                <TableCell align="right">Dostępne</TableCell>
                <TableCell align="right">Do przydzielenia</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((row: UnreconciledInvoiceRowDto) => (
                <TableRow key={row.invoiceId}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(row.invoiceId)}
                      onChange={() => toggleSelection(row.invoiceId)}
                      data-testid={`checkbox-${row.invoiceId}`}
                    />
                  </TableCell>
                  <TableCell>{row.invoiceNumber}</TableCell>
                  <TableCell>{row.issueDate}</TableCell>
                  <TableCell>{row.reservationId}</TableCell>
                  <TableCell>{row.buyerName}</TableCell>
                  <TableCell align="right">{fmt(row.totalGross)}</TableCell>
                  <TableCell align="right">{fmt(row.paidAmount)}</TableCell>
                  <TableCell align="right">{fmt(row.availableAmount)}</TableCell>
                  <TableCell align="right">{fmt(row.allocatedAmount)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openSingleDialog(row)}
                      data-testid={`reconcile-btn-${row.invoiceId}`}
                    >
                      Rozlicz
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
            <Typography variant="caption">
              Łącznie: {data.totalCount}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                disabled={(filters.pageNumber ?? 1) <= 1}
                onClick={() => handlePageChange((filters.pageNumber ?? 1) - 1)}
              >
                Poprzednia
              </Button>
              <Typography variant="caption" sx={{ alignSelf: 'center' }}>
                Strona {filters.pageNumber ?? 1}
              </Typography>
              <Button
                size="small"
                disabled={data.items.length < (filters.pageSize ?? 20)}
                onClick={() => handlePageChange((filters.pageNumber ?? 1) + 1)}
              >
                Następna
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Preview dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth data-testid="reconcile-preview-dialog">
        <DialogTitle>Podgląd rozliczenia</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Kwoty przydziału są obliczone przez serwer na podstawie dostępnej puli wpłat.
            Przy rozliczeniu grupowym kwoty mogą różnić się od podglądu.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nr FV</TableCell>
                <TableCell align="right">Do rozliczenia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.map((row) => (
                <TableRow key={row.invoiceId}>
                  <TableCell>{row.invoiceNumber}</TableCell>
                  <TableCell align="right">{fmt(row.allocatedAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {reconcileError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {reconcileError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isReconciling}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={confirmReconcile}
            disabled={isReconciling}
            data-testid="confirm-reconcile-btn"
          >
            {isReconciling ? 'Rozliczanie...' : 'Zatwierdź'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ReservationPaymentReconciliationPage;
